import React, { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, CircularProgress, Typography, Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import ReportShell from '../components/ReportShell';
import SeriesToggle, { useToggleSet } from '../components/SeriesToggle';
import { MeasureControls, useMeasure } from '../components/MeasureControls';
import useStats from '../useStats';
import { fmtMoney, fmtInt } from '../lib/pivot';
import { continuousByChannel, channelColour } from '../lib/periods';

const EMPTY = { labels: [], series: [] };

/**
 * Continuous monthly trend by channel — a stacked area (total made up of each
 * channel) or overlaid lines, across the whole timeline from MIN_YEAR. Channels
 * toggle on/off.
 */
export default function ChannelTrend() {
  const { stats, loading } = useStats();
  const measure = useMeasure('revenue');
  const [shape, setShape] = useState('area'); // 'area' (stacked) | 'line'

  const { labels, series } = stats ? continuousByChannel(stats, measure.get) : EMPTY;
  const channelToggle = useToggleSet(series.map((s) => s.channel));
  const shown = series.filter((s) => channelToggle.selected.has(s.channel));

  const controls = (
    <>
      <MeasureControls {...measure} />
      <ToggleButtonGroup size="small" exclusive value={shape} onChange={(_, v) => v && setShape(v)}>
        <ToggleButton value="area">Stacked area</ToggleButton>
        <ToggleButton value="line">Lines</ToggleButton>
      </ToggleButtonGroup>
    </>
  );

  let body;
  if (loading || !stats) {
    body = <CircularProgress />;
  } else if (!labels.length) {
    body = <Typography variant="body2" color="text.secondary">No data yet — import orders and Rebuild stats.</Typography>;
  } else {
    const fmt = measure.money ? fmtMoney : fmtInt;
    body = (
      <>
        <Box sx={{ mb: 1 }}>
          <SeriesToggle
            label="Channels"
            items={series.map((s) => ({ key: s.channel, label: s.label, colour: channelColour(s.channel) }))}
            selected={channelToggle.selected}
            onToggle={channelToggle.toggle}
            onAll={channelToggle.all}
            onNone={channelToggle.none}
          />
        </Box>
        <LineChart
          height={440}
          xAxis={[{ scaleType: 'point', data: labels }]}
          yAxis={[{ valueFormatter: (v) => fmt(v) }]}
          series={shown.map((s) => ({
            data: s.data,
            label: s.label,
            color: channelColour(s.channel),
            showMark: false,
            area: shape === 'area',
            stack: shape === 'area' ? 'total' : undefined,
            valueFormatter: (v) => (v == null ? '' : fmt(v)),
          }))}
          margin={{ left: 64, right: 16, top: 16, bottom: 24 }}
        />
      </>
    );
  }

  return <ReportShell title="Channel trend" controls={controls}>{body}</ReportShell>;
}
